export interface ProjectLink {
  type: 'github' | 'npm' | 'docs' | 'demo' | 'website' | 'appstore' | 'testflight';
  url: string;
  label: string;
}

export interface ProjectFeature {
  title: string;
  icon: string;
  items: string[];
}

export interface ProjectInstallation {
  title: string;
  description?: string;
  command?: string;
  codeBlock?: string;
  note?: string;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  icon: string;
  cardImage?: string;
  heroImage?: string;
  screenshots?: string[];
  shortDescription: string;
  fullDescription: string;
  techStack: string[];
  features: ProjectFeature[];
  useCases: string[];
  installations: ProjectInstallation[];
  links: ProjectLink[];
  featured: boolean;
  status: 'active' | 'beta' | 'deprecated' | 'archived';
  createdDate: string;
}

export const projects: Project[] = [
  {
    id: 'gimage',
    slug: 'gimage',
    name: 'Gimage',
    tagline: 'AI-Powered Image Generation & Processing',
    icon: 'image',
    cardImage: '/images/gimage_card_premium.webp',
    heroImage: '/images/gimage_hero_premium.webp',
    shortDescription: 'A versatile AI-powered tool built in pure Go offering three deployment modes: CLI, MCP Server, and AWS Lambda API. Single binary with zero dependencies.',
    fullDescription: 'A versatile AI-powered tool built in pure Go that combines **AI-powered image generation** with comprehensive image processing capabilities. Offers **three deployment modes**: command-line interface, MCP server for Claude integration, and serverless AWS Lambda API. Single static binary with zero external dependencies, supporting **macOS, Linux, and Windows** across multiple architectures. Defaults to **Gemini 3 Pro** for native 4K output and supports **six model families** across Gemini, Vertex AI, AWS Bedrock, and xAI — with per-provider features like CFG scale, configurable output format, and 13 aspect ratios.',
    techStack: [
      'Go',
      'Google Gemini',
      'Vertex AI',
      'AWS Bedrock',
      'xAI Grok',
      'AWS Lambda',
      'MCP Server'
    ],
    features: [
      {
        title: 'AI Image Generation',
        icon: 'palette',
        items: [
          'Multiple providers: Gemini, Vertex AI, AWS Bedrock, xAI Grok',
          'Models: Gemini 3 Pro (default, native 4K), Gemini 3.1 Flash-Lite, Gemini 2.5 Flash, Imagen 4 / Fast / Ultra, Nova Canvas, Grok Imagine',
          'Provider-specific options: CFG scale (Bedrock), output format (Vertex AI), 13 aspect ratios (Grok)',
          'Customizable size, style, quality, aspect ratio, negative prompts, and seeds',
          'Batch generation: 1-5 images per request'
        ]
      },
      {
        title: 'Image Processing',
        icon: 'zap',
        items: [
          'Resize, scale, crop, compress, and convert formats',
          'Support for PNG, JPEG, WebP, GIF, TIFF, BMP',
          'High-quality Lanczos resampling',
          'Preserves aspect ratios with percentage scaling'
        ]
      },
      {
        title: 'Three Deployment Modes',
        icon: 'rocket',
        items: [
          'CLI: Single portable binary, interactive TUI mode',
          'MCP Server: Integrates with Claude Desktop and AI assistants',
          'Lambda API: Serverless REST API on ARM64 Graviton2'
        ]
      },
      {
        title: 'Claude Integration',
        icon: 'bot',
        items: [
          'Native MCP server support',
          'Install via Homebrew, npm, or Claude Code CLI',
          'All generation and processing functions as tools'
        ]
      },
      {
        title: 'Flexible Pricing',
        icon: 'dollar',
        items: [
          'Gemini 2.5 Flash: $0.039/image',
          'Gemini 3 Pro: $0.134/image (1K/2K) · $0.24/image (4K)',
          'Imagen 4 Fast / Standard / Ultra: $0.02 / $0.04 / $0.06',
          'Nova Canvas: $0.04/image (\u2264 1024\u00d71024) · $0.08 (larger)',
          'Grok Imagine: $0.02/image · Pro $0.07/image'
        ]
      },
      {
        title: 'Developer Tools',
        icon: 'settings',
        items: [
          'Go SDK: github.com/apresai/gimage-go-sdk',
          'OpenAPI/Swagger documentation',
          'Lambda deployment tooling included'
        ]
      }
    ],
    useCases: [
      'Designers prototyping concepts with AI',
      'Developers automating image pipelines',
      'Content creators optimizing graphics',
      'ML engineers generating training datasets',
      'AI assistants needing image capabilities'
    ],
    installations: [
      {
        title: 'Installation',
        description: 'macOS, Linux, and Windows (via Homebrew - Recommended):',
        command: 'brew install apresai/tap/gimage'
      },
      {
        title: '',
        description: 'Manual Installation:',
        codeBlock: `# macOS (Apple Silicon)
curl -L https://github.com/apresai/gimage/releases/latest/download/gimage-darwin-arm64 -o gimage
chmod +x gimage && sudo mv gimage /usr/local/bin/

# Linux
curl -L https://github.com/apresai/gimage/releases/latest/download/gimage-linux-amd64 -o gimage
chmod +x gimage && sudo mv gimage /usr/local/bin/`
      },
      {
        title: 'Setup & Authentication',
        description: 'Get an API key from [Google AI Studio](https://aistudio.google.com/app/apikey), then configure:',
        codeBlock: `gimage auth setup gemini
# Or use environment variable (recommended)
export GEMINI_API_KEY="your-key"`,
        note: 'Check configuration with `gimage auth status` and `gimage auth list`. Supports Gemini, Vertex AI, AWS Bedrock, and xAI Grok — authenticate one or multiple providers.'
      },
      {
        title: 'CLI Usage Examples',
        description: 'Generate images:',
        codeBlock: `gimage generate "futuristic city at night"
gimage generate "abstract art" --size 1024x1024 --style photorealistic
gimage generate "forest scene" --negative "people, buildings"`
      },
      {
        title: '',
        description: 'Process images:',
        codeBlock: `gimage resize --input photo.jpg --width 800 --height 600
gimage scale --input photo.jpg --factor 0.5
gimage compress --input photo.jpg --quality 85
gimage convert --input photo.png --format jpg`
      },
      {
        title: 'MCP Server Setup for Claude Desktop',
        description: 'Add to your Claude Desktop MCP configuration:',
        codeBlock: `{
  "mcpServers": {
    "gimage": {
      "command": "gimage",
      "args": ["serve"],
      "env": {
        "GEMINI_API_KEY": "your-gemini-api-key-here"
      }
    }
  }
}`,
        note: 'Config locations: macOS: ~/Library/Application Support/Claude/claude_desktop_config.json, Linux: ~/.config/Claude/claude_desktop_config.json'
      }
    ],
    links: [
      {
        type: 'github',
        url: 'https://github.com/apresai/gimage',
        label: 'View on GitHub'
      },
      {
        type: 'docs',
        url: 'https://github.com/apresai/gimage#readme',
        label: 'Read Documentation'
      },
      {
        type: 'github',
        url: 'https://github.com/apresai/gimage/releases',
        label: 'Download Releases'
      },
      {
        type: 'npm',
        url: 'https://www.npmjs.com/package/@apresai/gimage-mcp',
        label: 'npm MCP Package'
      },
      {
        type: 'github',
        url: 'https://github.com/apresai/gimage-go-sdk',
        label: 'Go SDK'
      }
    ],
    featured: false,
    status: 'active',
    createdDate: '2024-01-15'
  },
  {
    id: 'podcaster',
    slug: 'podcaster',
    name: 'Podcaster',
    tagline: 'AI-Powered Podcast Platform',
    icon: 'audio',
    cardImage: '/images/podcaster_card.webp',
    heroImage: '/images/podcaster_hero.webp',
    shortDescription: 'Turn any content into a multi-host podcast via a web portal, Go CLI, or remote MCP server. Powered by Claude, Gemini, and Nova for scripts with six TTS providers for voice synthesis.',
    fullDescription: 'Podcaster is a full **AI-powered podcast platform** — available as a **web portal** at [podcasts.apresai.dev](https://podcasts.apresai.dev), a **Go CLI**, a **public REST API**, and a **remote MCP server** on AWS AgentCore. Paste a URL, upload a PDF, or provide text, and generate a natural multi-host conversation in minutes. The portal ships with **credit-based Stripe subscriptions** ($3 / $9 / $30 tiers), a voice browser across **55+ voices and six TTS providers**, and a public podcast directory. Under the hood, a **four-stage pipeline** (ingest → script → TTS → assembly) runs across **9 show formats** with automatic provider fallback and script quality review.',
    techStack: ['Go', 'Claude', 'Gemini', 'Nova', 'ElevenLabs', 'AWS Polly', 'Google Cloud TTS', 'FFmpeg', 'Stripe', 'AWS AgentCore', 'CloudFront'],
    features: [
      {
        title: 'Web Portal',
        icon: 'rocket',
        items: [
          'Quick Generate — paste a URL, one click, podcast in minutes',
          'Voice browser with audio preview across 55+ voices, 4 providers',
          'Public podcast directory at /podcasts with shareable episode pages',
          'Dashboard for tracking podcasts, usage, and credits',
          'API key management for CLI / Claude Desktop / Claude Code access'
        ]
      },
      {
        title: 'AI Script Generation',
        icon: 'bot',
        items: [
          'Claude Haiku 4.5, Claude Sonnet 4.6, Gemini 3.1 Flash-Lite, Gemini 3.1 Pro, or Nova 2 Lite',
          '9 show formats: conversation, interview, deep-dive, explainer, debate, news, storytelling, challenger, newscast',
          'Configurable tone (casual, technical, educational) and style',
          'Automatic script quality review and revision'
        ]
      },
      {
        title: 'Six TTS Providers',
        icon: 'audio',
        items: [
          'Gemini (AI Studio), Vertex AI Express, Vertex AI, ElevenLabs, Google Cloud TTS, AWS Polly',
          '55+ voices with cross-provider voice mixing across hosts',
          'Automatic fallback if a provider fails mid-synthesis',
          'Configurable speed, stability, and pitch per provider'
        ]
      },
      {
        title: 'Remote MCP Server',
        icon: 'bot',
        items: [
          'Public endpoint at podcasts.apresai.dev/mcp on AWS AgentCore',
          'Bearer-token auth via API keys issued in the portal',
          '6 tools: generate_podcast, get_podcast, list_podcasts, list_voices, list_options, server_info',
          'Works with Claude Code, Claude Desktop, and any MCP client'
        ]
      },
      {
        title: 'Public REST API',
        icon: 'settings',
        items: [
          'Bearer-token auth with credit-based billing per key',
          'Full OpenAPI spec with endpoints for generate, list, status, download',
          'Go SDK published at github.com/apresai/podcaster-sdk',
          'Audio delivered via CloudFront CDN'
        ]
      },
      {
        title: 'Persona System',
        icon: 'users',
        items: [
          'Built-in personas: Alex (host), Sam (analyst), Jordan (connector)',
          'Support for 1, 2, or 3 host conversations',
          'Customizable expertise areas and speaking styles',
          'Voice names automatically used as speaker labels'
        ]
      },
      {
        title: 'Pipeline Architecture',
        icon: 'terminal',
        items: [
          'Four-stage pipeline: Ingest → Script → TTS → Assembly',
          'Script-only mode (--script-only) to generate without audio',
          'Resume from script (--from-script) to regenerate audio',
          'Intermediate scripts and logs saved for debugging'
        ]
      }
    ],
    useCases: [
      'Content creators repurposing blog posts as podcasts',
      'Documentation teams making technical content accessible',
      'Educators converting course material to audio',
      'Marketers turning whitepapers into engaging content',
      'Anyone who wants to listen instead of read'
    ],
    installations: [
      {
        title: 'Web Portal (Recommended)',
        description: 'The fastest way to get started — sign in and generate in one click:',
        codeBlock: 'https://podcasts.apresai.dev',
        note: 'Sign in with Google. Credit-based subscription tiers: Starter $3/mo (10 credits), Creator $9/mo (30), Pro $30/mo (100). Credits roll over and never expire.'
      },
      {
        title: 'CLI',
        description: 'Install via Go:',
        codeBlock: `brew install ffmpeg
go install github.com/apresai/podcaster/cmd/podcaster@latest

# Set your API key
export GEMINI_API_KEY="your-gemini-api-key"

# Generate from a URL
podcaster generate -i https://example.com/article

# Or use the interactive wizard
podcaster generate --tui`,
        note: 'Requires Go 1.23+ and FFmpeg. Gemini is the default for script gen and TTS. Claude (ANTHROPIC_API_KEY) and ElevenLabs (ELEVENLABS_API_KEY) are optional.'
      },
      {
        title: 'MCP Server for Claude Code',
        description: 'Generate podcasts directly from Claude Code or Claude Desktop. First, create an API key in the [portal](https://podcasts.apresai.dev/api-keys), then:',
        codeBlock: `claude mcp add podcaster https://podcasts.apresai.dev/mcp \\
  --transport http \\
  --header "Authorization: Bearer pk_YOUR_API_KEY"`,
        note: 'For Claude Desktop, add an equivalent entry under mcpServers in your claude_desktop_config.json with transport: "streamable-http".'
      },
      {
        title: 'REST API',
        description: 'Integrate Podcaster into any app via the public REST API with bearer-token auth:',
        codeBlock: `# Start a generation
curl -X POST https://podcasts.apresai.dev/api/v1/podcasts \\
  -H "Authorization: Bearer pk_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"input_url":"https://example.com/article","duration":"standard"}'

# Poll for completion
curl https://podcasts.apresai.dev/api/v1/podcasts/{id} \\
  -H "Authorization: Bearer pk_YOUR_API_KEY"`,
        note: 'Go SDK available at github.com/apresai/podcaster-sdk. Full OpenAPI spec at podcasts.apresai.dev/docs.'
      }
    ],
    links: [
      { type: 'website', url: 'https://podcasts.apresai.dev', label: 'Web Portal' },
      { type: 'docs', url: 'https://podcasts.apresai.dev/docs', label: 'API Documentation' },
      { type: 'github', url: 'https://github.com/apresai/podcaster', label: 'View on GitHub' },
      { type: 'github', url: 'https://github.com/apresai/podcaster-sdk', label: 'Go SDK' },
      { type: 'github', url: 'https://github.com/apresai/podcaster/releases', label: 'Download Releases' }
    ],
    featured: true,
    status: 'active',
    createdDate: '2025-02-01'
  },
  {
    id: 'dangerously-skip',
    slug: 'dangerously-skip-permissions',
    name: 'dangerously skip',
    tagline: 'AI Safety, Speed, and the Moment You Stop Asking Permission',
    icon: 'terminal',
    cardImage: '/images/dangerously_skip_card.webp',
    heroImage: '/images/dangerously_skip_hero.webp',
    shortDescription: "A practitioner's confession about AI safety — exploring the tension between shipping fast and keeping humans in the loop, through essays and AI-generated podcasts.",
    fullDescription: "A reflective essay series examining **AI safety through a developer's lens**. Chronicles the journey from carefully approving every Claude Code action to bypassing safety checks with `--dangerously-skip-permissions`, and what that impulse reveals about how organizations adopt AI. Covers the **race to ship AI**, Anthropic's bet that **safety enables capability**, and the question from *Contact*: \"How did you survive your adolescence?\" Includes **two AI-generated podcasts** based on Dario Amodei's essays on AI's radical upside and technological adolescence.",
    techStack: ['Next.js', 'React', 'TypeScript', 'Podcaster'],
    features: [
      {
        title: 'Essays',
        icon: 'document',
        items: [
          '4 interconnected essays on AI safety and speed',
          '"Move Fast" — organizational pressure and the Amazon AI incident',
          '"The Skip" — from cautious user to --dangerously-skip-permissions',
          '"The Anthropic Bet" and "Surviving Our Adolescence"'
        ]
      },
      {
        title: 'AI Podcasts',
        icon: 'audio',
        items: [
          '2 episodes generated with Podcaster',
          'Based on Dario Amodei\'s "Machines of Loving Grace"',
          'And "The Adolescence of Technology"',
          'Multi-host AI conversations on existential AI themes'
        ]
      },
      {
        title: 'Terminal Aesthetic',
        icon: 'terminal',
        items: [
          'CLI-inspired design with monospace typography',
          'Green terminal accents and command-line visual language',
          'Inline code comments as section markers',
          'Design reinforces themes of automation and permissions'
        ]
      },
      {
        title: 'Real-World Cases',
        icon: 'zap',
        items: [
          'Amazon AI incident: hallucinated customer feedback',
          'Hospital scenario: AI clinical decision support failure',
          'Weapons systems analysis from a USAF veteran perspective',
          'Each case illustrates consequences of skipping safety'
        ]
      },
      {
        title: 'AI Safety Framework',
        icon: 'shield',
        items: [
          'Constitutional AI: principles over rigid rules',
          'Responsible Scaling Policy: red lines before capabilities emerge',
          'Mechanistic interpretability: understanding why models behave',
          "Anthropic's approach to surviving technological adolescence"
        ]
      }
    ],
    useCases: [
      'Developers reflecting on AI safety trade-offs',
      'Teams discussing responsible AI adoption policies',
      'Anyone curious about what happens when you skip the safety checks',
      "Readers interested in Anthropic's approach to AI safety"
    ],
    installations: [
      {
        title: 'Visit the Site',
        description: 'No installation needed — read the essays online:',
        codeBlock: 'https://dangerously-skip-permissions.apresai.dev/',
        note: 'Best experienced on desktop for the full terminal aesthetic.'
      }
    ],
    links: [
      { type: 'website', url: 'https://dangerously-skip-permissions.apresai.dev/', label: 'Read the Essays' }
    ],
    featured: false,
    status: 'archived',
    createdDate: '2025-03-01'
  },
  {
    id: 'sophies-trophies',
    slug: 'sophies-trophies',
    name: "Sophie's Trophies",
    tagline: 'AI-Powered Wine Discovery & Collection',
    icon: 'wine',
    cardImage: '/images/sophies_trophies_card.webp',
    heroImage: '/images/sophies_trophies_hero.webp',
    shortDescription: 'Snap a photo of any wine label and instantly get AI-powered identification, tasting notes, food pairings, and ratings. Build your personal wine collection.',
    fullDescription: "Sophie's Trophies is an **AI-powered wine discovery platform** that transforms how you explore and collect wines. Simply **snap a photo of any wine label**, and our AI instantly identifies it with detailed information including tasting notes, food pairings, ratings, and winery history. Features a conversational **AI sommelier (Sophie)** for personalized recommendations and an innovative **audio narration** feature that brings wine stories to life.",
    techStack: [
      'Claude Vision',
      'Google Gemini',
      'Go',
      'Next.js',
      'Flutter',
      'AWS Lambda',
      'DynamoDB'
    ],
    features: [
      {
        title: 'AI Wine Identification',
        icon: 'camera',
        items: [
          'Instant label recognition via Claude Vision',
          'Extracts name, winery, vintage, region, and grape varietals',
          'Works with any wine from any country',
          'Handles partial or damaged labels'
        ]
      },
      {
        title: 'Smart Enrichment',
        icon: 'sparkles',
        items: [
          'Automated research via Gemini with Google Search',
          'Tasting notes and flavor profiles',
          'Expert ratings aggregation',
          'Food pairing suggestions'
        ]
      },
      {
        title: 'Sophie AI Sommelier',
        icon: 'bot',
        items: [
          'Conversational wine assistant powered by Claude',
          'Personalized recommendations based on your collection',
          'Wine education and region exploration',
          'Answer any wine-related question'
        ]
      },
      {
        title: 'Audio Experience',
        icon: 'audio',
        items: [
          'AI-generated audio narrations for each wine',
          'Learn about wines hands-free',
          'Powered by Gemini TTS',
          'Perfect for wine tastings'
        ]
      },
      {
        title: 'Personal Collection',
        icon: 'collection',
        items: [
          'Save and organize your wines',
          'Write personal reviews and notes',
          'Track wines you want to try',
          'Share favorites with friends'
        ]
      },
      {
        title: 'Cross-Platform',
        icon: 'devices',
        items: [
          'Web app at sophiestrophies.com',
          'iOS app via TestFlight',
          'Sync across all devices',
          'Works offline with cached data'
        ]
      }
    ],
    useCases: [
      'Wine enthusiasts building collections',
      'Restaurant diners exploring wine lists',
      'Wine shop browsers seeking information',
      'Hosts planning dinner party pairings',
      'Learners exploring wine regions and varietals'
    ],
    installations: [
      {
        title: 'Web App',
        description: 'Visit the website to get started:',
        codeBlock: 'https://sophiestrophies.com',
        note: 'Sign in with Google to save wines to your collection.'
      },
      {
        title: 'iOS App (TestFlight)',
        description: 'The iOS app is currently available via TestFlight for beta testing. Contact us to join the beta program.',
        note: 'Requires iOS 16+ and iPhone with camera.'
      }
    ],
    links: [
      {
        type: 'website',
        url: 'https://sophiestrophies.com',
        label: 'Visit Website'
      },
      {
        type: 'docs',
        url: 'https://sophiestrophies.com/privacy',
        label: 'Privacy Policy'
      }
    ],
    featured: true,
    status: 'active',
    createdDate: '2024-06-01'
  },
  {
    id: 'regist',
    slug: 'regist',
    name: 're:Gist',
    tagline: 'AI-Powered Meeting Intelligence',
    icon: 'mic',
    cardImage: '/images/regist_card.webp',
    heroImage: '/images/regist_hero.webp',
    shortDescription: 'Record, transcribe, and summarize your meetings with AI. Get real-time transcription with speaker identification, structured summaries, and actionable follow-ups.',
    fullDescription: 're:Gist is an **AI-powered meeting intelligence app** that captures every detail from your meetings. Record audio on your iPhone, get **real-time transcription** with speaker diarization via Amazon Transcribe Streaming, and receive **structured AI summaries** powered by Amazon Bedrock. Automatically extracts **key topics, decisions, action items** with assignees and priorities. Export clean **markdown notes** for your wiki or project management tools.',
    techStack: ['Amazon Transcribe', 'Amazon Bedrock', 'Swift', 'SwiftUI', 'Next.js', 'AWS'],
    features: [
      {
        title: 'Real-Time Transcription',
        icon: 'mic',
        items: [
          'Amazon Transcribe Streaming for live speech-to-text',
          'Speaker diarization identifies who said what',
          'Works with any meeting format',
          'Record directly on iPhone'
        ]
      },
      {
        title: 'AI Summarization',
        icon: 'bot',
        items: [
          'Powered by Amazon Bedrock',
          'Extracts key topics and decisions',
          'Identifies open questions and concerns',
          'Structured, scannable summaries'
        ]
      },
      {
        title: 'Action Items',
        icon: 'zap',
        items: [
          'Automatic follow-up extraction',
          'Assignees and priorities identified',
          'Due date suggestions',
          'Never miss a commitment'
        ]
      },
      {
        title: 'Markdown Export',
        icon: 'document',
        items: [
          'Download meeting notes as clean markdown',
          'Integrates with wikis and project management',
          'Structured sections for easy scanning',
          'Share with your team instantly'
        ]
      }
    ],
    useCases: [
      'Product teams capturing meeting decisions',
      'Managers tracking action items across meetings',
      'Remote teams needing async meeting summaries',
      'Anyone who wants to focus on the conversation, not note-taking'
    ],
    installations: [
      {
        title: 'Web App',
        description: 'Sign in to start capturing meetings:',
        codeBlock: 'https://regist.apresai.dev/',
        note: 'Sign in with Google to get started.'
      }
    ],
    links: [
      { type: 'website', url: 'https://regist.apresai.dev/', label: 'Visit re:Gist' }
    ],
    featured: false,
    status: 'archived',
    createdDate: '2025-02-15'
  },
  {
    id: 'clipz',
    slug: 'clipz',
    name: 'Clipz',
    tagline: 'Intelligent Clipboard Manager for macOS',
    icon: 'clipboard',
    cardImage: '/images/clipz_card.webp',
    heroImage: '/images/clipz_hero.webp',
    shortDescription: 'A native macOS menu bar app that tracks your clipboard history — including images with thumbnail previews and files copied from Finder — with optional cloud sync across devices.',
    fullDescription: 'Clipz is a **native macOS clipboard manager** that lives in your menu bar and keeps track of everything you copy. Features **optional cloud sync** across all your Macs using Sign in with Apple or Google. Built with Swift and SwiftUI, Clipz is **local-first** (your data stays on your Mac by default) and **sandboxed** for security.',
    techStack: [
      'Swift',
      'SwiftUI',
      'AWS Cognito',
      'AWS AppSync',
      'DynamoDB',
      'S3'
    ],
    features: [
      {
        title: 'Clipboard History',
        icon: 'clipboard',
        items: [
          'Automatically saves your last 10 copied items in real time',
          'Text, rich text (HTML/RTF), links, and code snippets',
          'Image and screenshot support with thumbnail previews',
          'File path support — copy files from Finder with native icons'
        ]
      },
      {
        title: 'Cloud Sync',
        icon: 'cloud',
        items: [
          'Real-time sync across all your Macs via AWS AppSync',
          'Sign in with Apple or Google',
          'Zero configuration — auto-discovers service endpoints',
          'Connected Systems view shows which devices are contributing'
        ]
      },
      {
        title: 'Resilience',
        icon: 'rocket',
        items: [
          'Automatic retry with exponential backoff',
          'Crash recovery for pending sync items',
          'Network-aware — detects connectivity changes and adapts',
          'Sleep/wake smart — refreshes credentials after extended sleep'
        ]
      },
      {
        title: 'Privacy First',
        icon: 'shield',
        items: [
          'Local-first: data stays on your Mac by default',
          'Fully sandboxed for macOS security',
          'No analytics or tracking',
          'Per-user data isolation in cloud'
        ]
      },
      {
        title: 'Quick Access',
        icon: 'zap',
        items: [
          'Unobtrusive scissors icon in your menu bar with item count badge',
          'Number key shortcuts (1-9, 0) to copy items instantly from the panel',
          'Automatic dark/light mode support',
          'Click any item to copy it back'
        ]
      },
      {
        title: 'Account Deletion',
        icon: 'trash',
        items: [
          'Full account deletion from Settings',
          'Removes all cloud data',
          'App Store compliant (5.1.1)',
          'GDPR-friendly'
        ]
      }
    ],
    useCases: [
      'Developers copying code snippets',
      'Writers collecting research notes',
      'Designers saving color codes and assets',
      'Anyone who copies and pastes frequently',
      'Users who work across multiple Macs'
    ],
    installations: [
      {
        title: 'Mac App Store',
        description: 'Download Clipz from the Mac App Store:',
        codeBlock: 'https://apps.apple.com/us/app/clipz-clipboard-manager/id6754185331?mt=12',
        note: 'Requires macOS 13.0 (Ventura) or later.'
      },
      {
        title: 'Build from Source',
        description: 'Clone and build locally with Xcode 15+:',
        codeBlock: `git clone https://github.com/chadneal/clipz.git
cd clipz
make build       # Debug build
make release     # Release build`,
        note: 'Requires macOS 13.0+, Xcode 15.0+, and AWS CLI for backend deployment.'
      }
    ],
    links: [
      {
        type: 'appstore',
        url: 'https://apps.apple.com/us/app/clipz-clipboard-manager/id6754185331?mt=12',
        label: 'Download on App Store'
      },
      {
        type: 'website',
        url: 'https://apresai.dev/clipz',
        label: 'Product Page'
      },
      {
        type: 'docs',
        url: 'https://apresai.dev/clipz/privacy',
        label: 'Privacy Policy'
      },
      {
        type: 'docs',
        url: 'https://apresai.dev/clipz/support',
        label: 'Support'
      }
    ],
    featured: false,
    status: 'active',
    createdDate: '2025-01-01'
  },
  {
    id: 'voiceprd',
    slug: 'voiceprd',
    name: 'VoicePRD',
    tagline: 'Voice-First Product Requirements',
    icon: 'mic',
    cardImage: '/images/voiceprd_card.webp',
    heroImage: '/images/voiceprd_hero.webp',
    shortDescription: 'Transform voice conversations into professional PRDs. Talk through your product ideas with an AI assistant and export to Generic Markdown, Kiro Spec, or CLAUDE.md.',
    fullDescription: 'VoicePRD is an **iOS app that transforms voice conversations into professional Product Requirements Documents**. Instead of typing out product ideas, simply talk through them with an AI assistant powered by **AWS Nova Sonic 2**, and the app generates a structured PRD you can export and share. The AI guides you through **8 product topics** — Problem, Users, User Stories, Technical Context, Features, Quality, Code Preferences, and Success Metrics — with **topic depth scoring** that warns when coverage is shallow before generation. A **hub-and-spoke Project Mode** lets you complete topics in independent voice sessions and generate the PRD when ready. Output in **three formats**: Generic Markdown, Kiro Spec (requirements/design/tasks), or CLAUDE.md.',
    techStack: [
      'AWS Nova Sonic 2',
      'Claude Sonnet 4.6',
      'Claude Haiku 4.5',
      'AWS Bedrock',
      'Swift',
      'SwiftUI',
      'SwiftData',
      'AWS Cognito',
      'Apple Sign-In'
    ],
    features: [
      {
        title: 'Voice-First Experience',
        icon: 'mic',
        items: [
          'No typing required—entire flow through natural conversation',
          'Real-time bidirectional voice with AWS Nova Sonic 2',
          'Interrupt the AI anytime by speaking',
          'Hands-free operation with mute toggle'
        ]
      },
      {
        title: 'Intelligent Guidance',
        icon: 'bot',
        items: [
          'AI guides you through 8 product topics: Problem, Users, User Stories, Technical Context, Features, Quality, Code Preferences, Success Metrics',
          'Probing questions to clarify your thinking',
          'Topic depth scoring warns about shallow coverage before generation',
          'Natural topic transitions when ready'
        ]
      },
      {
        title: 'Project Workflow',
        icon: 'document',
        items: [
          'Hub-and-spoke Project Mode: complete topics in independent voice sessions',
          'Per-topic depth scores on a dashboard',
          'Generate PRD when all 8 topics are ready — or generate early with partial coverage',
          'Legacy single-session mode also available'
        ]
      },
      {
        title: 'Multi-Format PRD Output',
        icon: 'sparkles',
        items: [
          'Generic Markdown — 12-section PRD with vision, users, stories, features, quality',
          'Kiro Spec — three-document form (requirements.md + design.md + tasks.md) for AI-assisted dev',
          'CLAUDE.md — concise project guidelines for Claude Code',
          'Pipeline: Haiku extraction → Sonnet synthesis → validator → renderer'
        ]
      },
      {
        title: 'Real-Time Visualization',
        icon: 'waveform',
        items: [
          'Dual waveform display (green: you, purple: AI)',
          'Visual status: Listening, Thinking, Speaking',
          'Progress dots show topic completion',
          'Intuitive state feedback throughout'
        ]
      },
      {
        title: 'Export & Share',
        icon: 'share',
        items: [
          'Save to Files app (iCloud, local)',
          'Google Drive integration',
          'Copy to clipboard',
          'Share via any iOS app'
        ]
      },
      {
        title: 'Privacy & Security',
        icon: 'shield',
        items: [
          'No audio stored on servers—real-time streaming only',
          'Apple Sign-In authentication',
          'Conversations stored locally on device',
          'AWS Cognito identity management'
        ]
      }
    ],
    useCases: [
      'Product managers capturing early-stage concepts',
      'Entrepreneurs brainstorming startup ideas on the go',
      'Innovation teams documenting feature proposals',
      'Anyone who thinks better out loud than typing',
      'Commuters capturing product ideas hands-free'
    ],
    installations: [
      {
        title: 'iOS App',
        description: 'VoicePRD is available for iOS devices:',
        note: 'Requires iOS 17+ and microphone permission. Sign in with your Apple ID to get started.'
      },
      {
        title: 'Getting Started',
        description: 'Quick start guide:',
        codeBlock: `1. Download VoicePRD from the App Store
2. Sign in with your Apple ID or Google
3. Create a project — the dashboard shows all 8 topics
4. Tap a topic to start a voice conversation
5. Complete each topic at your own pace (depth scores show coverage)
6. Generate your PRD in Markdown, Kiro Spec, or CLAUDE.md format
7. Export to Files, Google Drive, or share directly`,
        note: 'Optionally connect Google Drive in Settings for cloud export.'
      }
    ],
    links: [
      {
        type: 'testflight',
        url: 'https://testflight.apple.com/join/PLACEHOLDER',
        label: 'Join TestFlight Beta'
      },
      {
        type: 'docs',
        url: 'https://apresai.dev/voiceprd/privacy',
        label: 'Privacy Policy'
      }
    ],
    featured: false,
    status: 'archived',
    createdDate: '2025-01-25'
  },
  {
    id: 'ftw',
    slug: 'ftw',
    name: 'FTW (For The Win)',
    tagline: 'Step Tracking Competitions for Friends',
    icon: 'trophy',
    cardImage: '/images/ftw_card.webp',
    heroImage: '/images/ftw_hero.webp',
    shortDescription: 'A step tracking competition app for Apple Watch + iPhone. Small-group step leaderboards for friends, families, and coworkers. One thing, done beautifully.',
    fullDescription: 'FTW turns daily steps into friendly competition. Create private groups, invite friends via shareable codes, and compete on **real-time leaderboards** powered by HealthKit. Features include **62 achievement badges** with milestone tier progression, **cheers and taunts**, weekly/monthly champions, and streak tracking. Powered by a **serverless Go backend on AWS Lambda ARM64** with DynamoDB single-table design, and a native **Apple Watch companion app** for glanceable stats.',
    techStack: [
      'Swift',
      'SwiftUI',
      'HealthKit',
      'WatchKit',
      'Go',
      'AWS Lambda',
      'DynamoDB',
      'EventBridge',
      'APNs',
      'Next.js'
    ],
    features: [
      {
        title: 'Real-Time Leaderboards',
        icon: 'bar-chart-2',
        items: [
          'Daily, weekly, and monthly step competitions',
          'Live rank changes with push notifications',
          'Tie-breaking with alphabetical ordering',
          'Group leaderboard snapshots for history'
        ]
      },
      {
        title: '62 Achievement Badges',
        icon: 'award',
        items: [
          '9 categories: steps, distance, streaks, competition, social, and more',
          '36 repeatable badges with milestone tier progression (bronze/silver/gold)',
          'Near-miss framing to drive re-engagement',
          'Milestone celebrations with haptic feedback'
        ]
      },
      {
        title: 'Apple Watch Companion',
        icon: 'watch',
        items: [
          'Glanceable step count and rank',
          'Background HealthKit sync',
          'Complications for watch faces',
          'Automatic step delivery via WatchConnectivity'
        ]
      },
      {
        title: 'Social Features',
        icon: 'heart',
        items: [
          'Cheers and taunts with custom messages',
          'Auto-taunts when you overtake someone',
          'Group invite codes (6-char, shareable)',
          'Block and report for safety'
        ]
      },
      {
        title: 'Serverless Backend',
        icon: 'cloud',
        items: [
          'Go Lambda functions on ARM64 Graviton2 (provided.al2023)',
          '24 REST API endpoints via API Gateway, plus a dedicated step-sync Lambda',
          'Leaderboard compute runs every 5 minutes via EventBridge schedule',
          'DynamoDB single-table design with GSIs; ULID IDs for sortable keys',
          'Sign in with Apple + Google Sign-In with backend-issued JWTs'
        ]
      },
      {
        title: 'Smart Notifications',
        icon: 'bell',
        items: [
          'Rank change alerts and overtake notifications',
          'Badge earned and milestone celebrations',
          'Weekly recap summaries',
          'Timezone-aware quiet hours'
        ]
      }
    ],
    useCases: [
      'Friends competing on daily step counts',
      'Families staying active together',
      'Coworker wellness challenges',
      'Personal motivation through streak tracking',
      'Casual fitness accountability groups'
    ],
    installations: [
      {
        title: 'iOS App',
        description: 'FTW is available for iPhone and Apple Watch:',
        note: 'Requires iOS 17+, watchOS 10+. Sign in with Apple ID or Google to get started.'
      },
      {
        title: 'Getting Started',
        description: 'Quick start guide:',
        codeBlock: `1. Download FTW from the App Store
2. Sign in with Apple or Google
3. Create a group or join one with an invite code
4. Grant HealthKit step permissions
5. Start walking — your steps sync automatically
6. Check the leaderboard to see where you rank`,
        note: 'Apple Watch companion app installs automatically when the iPhone app is installed.'
      }
    ],
    links: [
      {
        type: 'appstore',
        url: 'https://apps.apple.com/app/ftw-for-the-win/id6759205235',
        label: 'App Store'
      },
      {
        type: 'website',
        url: 'https://ftw.apresai.dev',
        label: 'Website'
      },
      {
        type: 'docs',
        url: '/ftw/badges.html',
        label: 'Badge Catalog'
      }
    ],
    featured: true,
    status: 'active',
    createdDate: '2025-02-01'
  },
  {
    id: '2ndbrain',
    slug: '2ndbrain',
    name: '2ndbrain',
    tagline: 'AI-Native Markdown Knowledge Base',
    icon: 'brain',
    cardImage: '/images/2ndbrain_card.webp',
    heroImage: '/images/2ndbrain_hero.webp',
    shortDescription: 'A Go CLI, MCP server, and native macOS editor that share a SQLite index. Hybrid search combining BM25 keyword matching with vector semantic search.',
    fullDescription: '2ndbrain is an **AI-native markdown knowledge base** with semantic search. A **Go CLI**, **MCP server**, and **native macOS editor** all share a single SQLite index, enabling both users and AI assistants to search the same indexed knowledge base. Features **hybrid search** combining BM25 keyword matching with vector semantic search using Reciprocal Rank Fusion, **RAG-based Q&A** with source citations, an **MCP server exposing 16 tools** for AI coding assistants, and AI-native editor features like **Polish** (AI copy-editor with diff preview) and **Suggest Links** (semantic wikilink suggestions). Choose from three AI providers: **AWS Bedrock**, **OpenRouter**, or **Ollama** (fully local). Vaults are fully portable with dimension-break detection when providers change. Supports typed frontmatter with schema validation, wikilinks with graph traversal, document templates (ADR, runbook, PRD, prfaq, postmortem, note), and full **Obsidian vault compatibility**.',
    techStack: [
      'Go',
      'Swift',
      'SwiftUI',
      'SQLite',
      'MCP Server',
      'AWS Bedrock',
      'OpenRouter',
      'Ollama'
    ],
    features: [
      {
        title: 'Hybrid Search',
        icon: 'search',
        items: [
          'BM25 keyword matching + vector semantic search',
          'Reciprocal Rank Fusion for optimal result blending',
          'Three embedding providers: Bedrock, OpenRouter, Ollama',
          'Configurable similarity threshold to tune noise vs recall'
        ]
      },
      {
        title: 'RAG Question-Answering',
        icon: 'bot',
        items: [
          'Ask natural language questions about your knowledge base',
          'Source citations with document references',
          'Multiple LLM providers for generation',
          'Dimension-break detection surfaces mismatches instead of silent bad results'
        ]
      },
      {
        title: 'MCP Server',
        icon: 'terminal',
        items: [
          '16 tools for AI coding assistants',
          'kb_info, kb_search, kb_ask, kb_read, kb_list, kb_create, kb_update_meta, kb_related, kb_structure, kb_delete, kb_index',
          'kb_suggest_links, kb_polish, kb_git_activity, kb_git_diff, kb_git_status',
          'Sidecar status files + live observability panel show connected clients'
        ]
      },
      {
        title: 'AI-Native Editing',
        icon: 'sparkles',
        items: [
          'Polish — AI copy-editor with accept/reject diff preview (Cmd+Option+P)',
          'Suggest Links — click-to-insert semantic wikilink suggestions (Cmd+Shift+L)',
          'Incremental re-embed on save — only the changed document is re-indexed',
          'Merge conflict resolution with 3-way diff when files change externally'
        ]
      },
      {
        title: 'Model Catalog & Benchmarking',
        icon: 'zap',
        items: [
          '`2nb models list` — verified catalog across Bedrock, OpenRouter, Ollama',
          '`2nb models test` — smoke-test any model before switching',
          '`2nb models bench` — benchmark favorites with persistent history',
          '`2nb models bench compare` — side-by-side latency leaderboard'
        ]
      },
      {
        title: 'Skill Files',
        icon: 'bot',
        items: [
          'One command teaches AI coding agents about your vault',
          'Supports 8 agents: Claude Code, Cursor, Windsurf, GitHub Copilot, Kiro, Cline, Roo Code, JetBrains Junie',
          '`2nb skills install --all` to install for every detected agent',
          'Install per-project or globally with --user'
        ]
      },
      {
        title: 'Document System',
        icon: 'document',
        items: [
          'Templates: ADR, runbook, PRD, prfaq, postmortem, note',
          'Typed frontmatter with schema validation',
          'Status state machines (draft → review → approved)',
          'Wikilink resolution and graph traversal'
        ]
      },
      {
        title: 'Native macOS Editor',
        icon: 'devices',
        items: [
          'SwiftUI + AppKit with Source / Split / Preview modes',
          'Vault Health panel with one-click Rebuild Index + Re-embed All',
          'MCP Server Status (Cmd+Shift+M), Recent Activity (Cmd+Shift+G), Ask AI (Cmd+Shift+A)',
          'Git sidebar indicators (modified / untracked) and right-click Show Changes vs HEAD',
          'Autosave with crash recovery, requires macOS 14+'
        ]
      },
      {
        title: 'Local-First & Obsidian Compatible',
        icon: 'shield',
        items: [
          'Plain markdown files you own — tar and move any machine',
          'SQLite with WAL mode; embeddings stored as raw float32 BLOBs',
          'Full Obsidian vault import/export',
          'Ollama provider for fully offline usage'
        ]
      }
    ],
    useCases: [
      'Engineers building a searchable team knowledge base',
      'AI coding assistants that need project context via MCP',
      'Teams managing ADRs, runbooks, and postmortems',
      'Obsidian users who want semantic search and RAG',
      'Anyone who wants AI answers grounded in their own notes'
    ],
    installations: [
      {
        title: 'Installation',
        description: 'Install via Homebrew cask (installs CLI + native macOS editor):',
        command: 'brew install --cask apresai/tap/secondbrain'
      },
      {
        title: '',
        description: 'Or install the CLI only:',
        command: 'brew install apresai/tap/2nb',
        note: 'The editor is ad-hoc signed. On first launch, right-click the app and choose "Open" to bypass Gatekeeper. Binaries also available on GitHub Releases. Go 1.24+ required if building from source.'
      },
      {
        title: 'Quick Start',
        description: 'Initialize a vault, configure AI, and start searching:',
        codeBlock: `2nb init --path ~/vault
2nb ai setup                          # interactive provider + model wizard
2nb create "Title" --type adr
2nb index
2nb search "query"
2nb ask "question about your notes"`
      },
      {
        title: 'MCP Server Setup for Claude Code',
        description: 'Add to your ~/.claude.json:',
        codeBlock: `{
  "mcpServers": {
    "2ndbrain": {
      "command": "2nb",
      "args": ["mcp-server"],
      "cwd": "/path/to/vault"
    }
  }
}`,
        note: 'Exposes 16 tools (full list in the MCP Server feature above). Run `2nb mcp-setup` for Cursor, Claude Desktop, Gemini CLI, Amazon Q, and Kiro config snippets.'
      },
      {
        title: 'AI Provider Setup',
        description: 'Choose your AI provider (or just run `2nb ai setup` for the interactive wizard):',
        codeBlock: `# AWS Bedrock (uses existing AWS SSO — zero new keys)
2nb config set ai.provider bedrock

# OpenRouter
export OPENROUTER_API_KEY="sk-or-..."
2nb config set ai.provider openrouter

# Ollama (fully local, no API key needed)
brew install ollama
ollama pull embeddinggemma
ollama pull qwen2.5:0.5b
2nb config set ai.provider ollama`,
        note: 'All commands support --json, --yaml, --csv output formats. Bedrock supports any Converse API model (Claude, Nova, Llama, Mistral).'
      }
    ],
    links: [
      {
        type: 'github',
        url: 'https://github.com/apresai/2ndbrain',
        label: 'View on GitHub'
      },
      {
        type: 'docs',
        url: 'https://github.com/apresai/2ndbrain#readme',
        label: 'Read Documentation'
      },
      {
        type: 'github',
        url: 'https://github.com/apresai/2ndbrain/releases',
        label: 'Download Releases'
      }
    ],
    featured: true,
    status: 'active',
    createdDate: '2025-04-01'
  },
  {
    id: 'reelz',
    slug: 'reelz',
    name: 'Reelz',
    tagline: 'Local Menu-Bar Screen Recorder for macOS',
    icon: 'zap',
    cardImage: '/images/reelz_card.webp',
    heroImage: '/images/reelz_hero.webp',
    screenshots: [
      '/images/reelz_shot_01.webp',
      '/images/reelz_shot_02.webp',
      '/images/reelz_shot_03.webp',
      '/images/reelz_shot_04.webp',
    ],
    shortDescription: 'A menu-bar-only macOS screen recorder. Capture a window or the full screen and save a small MP4, WebP, or GIF clip — entirely on your Mac. No cloud, no account, no tracking.',
    fullDescription: 'Reelz is a **menu-bar-only macOS screen recorder**. Record a single window or your whole screen and it saves a compact **MP4, WebP, or GIF** clip straight to a folder you choose. Everything runs **on your Mac** — there is no cloud upload, no account, and no analytics. The only system access it needs is **Screen Recording** permission. Built natively with Swift, SwiftUI, and ScreenCaptureKit, with in-process encoding via Apple VideoToolbox plus statically linked libwebp and cgif. Reelz is **free** and distributed as a notarized DMG, provided **as-is, without warranty**.',
    techStack: [
      'Swift',
      'SwiftUI',
      'AppKit',
      'ScreenCaptureKit',
      'AVFoundation',
      'VideoToolbox',
      'libwebp',
      'cgif'
    ],
    features: [
      {
        title: 'Fully Local & Private',
        icon: 'shield',
        items: [
          'All recording and encoding happen on your Mac',
          'No cloud upload, no account, no sign-in',
          'No analytics, telemetry, or tracking of any kind',
          'Clips are written straight to a folder you choose'
        ]
      },
      {
        title: 'Capture Anything',
        icon: 'camera',
        items: [
          'Record the full screen or a single window',
          'Built-in window picker — no system "Stop Sharing" bar',
          'One-click stop: click the menu-bar icon to end a recording',
          'Global keyboard shortcuts for record and stop'
        ]
      },
      {
        title: 'Small, Shareable Clips',
        icon: 'zap',
        items: [
          'Save as MP4, WebP, or animated GIF',
          'On-device encoding via VideoToolbox, libwebp, and cgif',
          'Tiny files — a 6-second WebP clip is around 0.7 MB',
          'Copy a clip to the clipboard or reveal it in Finder'
        ]
      },
      {
        title: 'Free & Effortless',
        icon: 'sparkles',
        items: [
          'Free, notarized, and signed with a Developer ID',
          'Lives entirely in the menu bar — no Dock icon, no clutter',
          'Optional launch at login',
          'Provided as-is, with no account to manage'
        ]
      }
    ],
    useCases: [
      'Capturing quick bug repros to share in a PR or Slack',
      'Recording short how-to and demo clips',
      'Grabbing a GIF of a UI interaction',
      'Anyone who wants a private, no-cloud screen recorder'
    ],
    installations: [
      {
        title: 'Download',
        description: 'Reelz is a free, notarized DMG for macOS:',
        codeBlock: 'https://apresai.dev/downloads/reelz.dmg',
        note: 'Requires macOS 15 (Sequoia) or later on Apple Silicon. On first launch, grant Screen Recording access in System Settings > Privacy & Security > Screen Recording, then use "Quit & Reopen Reelz" so the permission takes effect.'
      },
      {
        title: 'First Recording',
        description: 'Once installed:',
        codeBlock: `1. Click the Reelz icon in your menu bar
2. Choose "Record Full Screen" or "Record Window..."
3. Click the menu-bar icon again to stop
4. Your clip is saved to ~/Movies/Reelz (or a folder you choose)`,
        note: 'Pick MP4, WebP, or GIF and a quality profile in Settings.'
      }
    ],
    links: [
      {
        type: 'website',
        url: 'https://apresai.dev/downloads/reelz.dmg',
        label: 'Download for macOS'
      },
      {
        type: 'docs',
        url: 'https://apresai.dev/projects/reelz/privacy',
        label: 'Privacy & Disclaimer'
      }
    ],
    featured: true,
    status: 'active',
    createdDate: '2026-06-01'
  }
];

export const getFeaturedProjects = (): Project[] => {
  return projects.filter(p => p.featured);
};

export const getProjectBySlug = (slug: string): Project | undefined => {
  return projects.find(p => p.slug === slug);
};

export const getActiveProjects = (): Project[] => {
  return projects.filter(p => p.status === 'active');
};
